'use client'

import { PROJECT_COPYRIGHT, SOCIAL_LINKS } from "@shared/constants"
import { Mail, MessageSquare } from "lucide-react"
import Link from "next/link"
import { useWhatsAppUrl } from "../../hooks"
import { SiteFooterLegal } from "./SiteFooterLegal"
import { PhilaBrandLockup } from "../brand/PhilaBrandLockup"

const SOCIAL_ICONS: Record<string, React.ReactNode> = {
  Email: <Mail className="h-6 w-6" />,
  WhatsApp: <MessageSquare className="h-6 w-6" />
}

/**
 * Props for an individual social media link in the footer.
 */
interface SocialLinkProps {
  /** URL to link to */
  href: string
  /** Icon React node to render */
  icon: React.ReactNode
  /** Accessible label for screen readers */
  label: string
}

/**
 * SocialLink component renders a styled external link with an icon.
 * It automatically replaces the href with WhatsApp URL from hook if label is "WhatsApp".
 * 
 * @param href - The link URL
 * @param icon - The icon to display inside the link
 * @param label - The accessible label for the link
 */
const SocialLink = ({ href, icon, label }: SocialLinkProps) => {
  const whatsAppUrl = useWhatsAppUrl()
  const finalHref = label === "WhatsApp" ? whatsAppUrl : href
  return (
    <Link
      href={finalHref}
      className="text-brand-dark/70 hover:text-brand-secondary transition-colors"
      target="_blank"
      rel="noopener noreferrer"
    >
      {icon}
      <span className="sr-only">{label}</span>
    </Link>
  )
}

/**
 * SiteFooter component renders the website footer including branding,
 * copyright, description text, social media links, and legal information.
 */
export function SiteFooter() {
  return (
    <footer className="w-full">
      {/* Main Footer */}
      <div className="border-t border-brand/10 bg-white">
        <div className="container py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <PhilaBrandLockup variant="compact" className="text-brand" />
              </div>
              <p className="mt-2 text-xs text-brand-dark/50">
                &copy; {new Date().getFullYear()} {PROJECT_COPYRIGHT}
              </p>
            </div>
            <div className="flex items-center space-x-6">
              {SOCIAL_LINKS.map((link) => (
                <SocialLink
                  key={link.label}
                  icon={SOCIAL_ICONS[link.label]}
                  {...link}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Legal Footer */}
      <SiteFooterLegal />
    </footer>
  )
}
